<template>
  <HeaderComponent />

  <HeroComponent 
    image="https://images.pexels.com/photos/7688336/pexels-photo-7688336.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2" 
    text="Get In Touch" 
    subtitle="Ready to bring your vision to life? Let's start a conversation and create something amazing together."
  />

  <!-- Contact Intro Section -->
  <div class="py-20">
    <div class="container mx-auto px-4">
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <div data-sal="slide-up" data-sal-delay="200">
          <h6 class="text-sm font-medium uppercase tracking-wider text-secondary mb-4">LET'S TALK</h6>
          <h2 class="text-4xl md:text-5xl font-bold font-heading text-gray-900 mb-6">
            Let's Create Something Great Together
          </h2>
          <p class="text-xl text-gray-600 leading-relaxed mb-8">
            Big ideas start with a simple conversation. Whether you have a clear vision or just a spark of an idea, 
            we're here to help bring it to life with creativity, expertise, and passion.
          </p>
          
          <!-- Key Benefits -->
          <div class="space-y-4">
            <div class="flex items-center">
              <div class="w-8 h-8 bg-secondary rounded-full flex items-center justify-center mr-4">
                <svg class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
              </div>
              <span class="text-gray-700 font-medium">Free consultation to discuss your project</span>
            </div>
            <div class="flex items-center">
              <div class="w-8 h-8 bg-secondary rounded-full flex items-center justify-center mr-4">
                <svg class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
              </div>
              <span class="text-gray-700 font-medium">Detailed project proposal and timeline</span>
            </div>
            <div class="flex items-center">
              <div class="w-8 h-8 bg-secondary rounded-full flex items-center justify-center mr-4">
                <svg class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
              </div>
              <span class="text-gray-700 font-medium">Quick response within 24 hours</span>
            </div>
          </div>
        </div>

        <!-- Contact Info Cards -->
        <div class="space-y-6" data-sal="slide-up" data-sal-delay="400">
          <div 
            v-for="info in contactInfo" 
            :key="info.title"
            class="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
          >
            <div class="flex items-start">
              <div class="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center mr-6 flex-shrink-0">
                <i :class="`${info.icon} text-white text-xl`"></i>
              </div>
              <div>
                <h3 class="text-xl font-bold font-heading text-gray-900 mb-2">{{ info.title }}</h3>
                <p class="text-gray-600 mb-3">{{ info.description }}</p>
                <a 
                  :href="info.link" 
                  class="text-primary hover:text-secondary font-medium transition-colors duration-300"
                  :target="info.external ? '_blank' : ''"
                >
                  {{ info.value }}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Contact Form Section -->
  <div class="py-20 bg-gray-50">
    <div class="container mx-auto px-4">
      <div class="max-w-4xl mx-auto">
        <div class="text-center mb-16" data-sal="slide-up" data-sal-delay="200">
          <h6 class="text-sm font-medium uppercase tracking-wider text-secondary mb-4">SEND MESSAGE</h6>
          <h2 class="text-4xl md:text-5xl font-bold font-heading text-gray-900 mb-6">
            Tell Us About Your Project
          </h2>
          <p class="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Fill out the form below and we'll get back to you within 24 hours with a detailed response.
          </p>
        </div>

        <div class="bg-white rounded-3xl shadow-2xl p-8 md:p-12" data-sal="slide-up" data-sal-delay="400">
          <form @submit.prevent="sendEmail" class="space-y-6">
            <!-- Name and Email Row -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label for="name" class="block text-sm font-medium text-gray-700 mb-2">Your Name *</label>
                <input 
                  type="text" 
                  id="name" 
                  name="name" 
                  v-model="form.name" 
                  required
                  class="w-full px-4 py-3 border border-[#d1d5db] rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-300"
                  placeholder="John Doe"
                  aria-describedby="name-help"
                  autocomplete="name"
                >
                <span id="name-help" class="sr-only">Enter your full name</span>
              </div>
              <div>
                <label for="email" class="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                <input 
                  type="email" 
                  id="email" 
                  name="email" 
                  v-model="form.email" 
                  required
                  class="w-full px-4 py-3 border border-[#d1d5db] rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-300"
                  placeholder="john@example.com"
                  aria-describedby="email-help"
                  autocomplete="email"
                >
                <span id="email-help" class="sr-only">Enter your email address</span>
              </div>
            </div>

            <!-- Company Row -->
            <div>
              <label for="company" class="block text-sm font-medium text-gray-700 mb-2">Company (Optional)</label>
              <input 
                type="text" 
                id="company" 
                name="company" 
                v-model="form.company"
                class="w-full px-4 py-3 border border-[#d1d5db] rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-300"
                placeholder="Your Company Name"
              >
            </div>

            <!-- Service Type -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-3">Service Needed</label>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <label 
                  v-for="service in services" 
                  :key="service.value"
                  class="flex items-center p-4 border border-[#d1d5db] rounded-lg cursor-pointer hover:border-primary transition-colors duration-300"
                  :class="{ 'border-primary bg-primary/5': form.services.includes(service.value) }"
                >
                  <input 
                    type="checkbox" 
                    :value="service.value" 
                    v-model="form.services"
                    class="mr-3"
                  >
                  <span class="font-medium">{{ service.label }}</span>
                </label>
              </div>
            </div>

            <!-- Message -->
            <div>
              <label for="message" class="block text-sm font-medium text-gray-700 mb-2">Tell us about your project *</label>
              <textarea 
                name="message" 
                id="message" 
                rows="6"
                v-model="form.message" 
                required
                class="w-full px-4 py-3 border border-[#d1d5db] rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-300 resize-none"
                placeholder="Describe your project, goals, timeline, and any specific requirements..."
                aria-describedby="message-help"
              ></textarea>
              <span id="message-help" class="sr-only">Describe your project requirements and goals in detail</span>
            </div>

            <!-- Submit Button -->
            <div class="text-center">
              <button 
                type="submit" 
                :disabled="loading"
                class="inline-flex items-center px-8 py-4 bg-gradient-to-r from-primary to-secondary text-white font-medium rounded-lg hover:shadow-lg transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span v-if="!loading">Send Message</span>
                <span v-else class="flex items-center">
                  <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending...
                </span>
              </button>
            </div>

            <!-- Success/Error Messages -->
            <div v-if="successMessage || errorMessage" class="text-center">
              <div v-if="successMessage" class="bg-green-50 border border-green-200 text-green-800 px-6 py-4 rounded-lg">
                {{ successMessage }}
              </div>
              <div v-if="errorMessage" class="bg-red-50 border border-red-200 text-red-800 px-6 py-4 rounded-lg">
                {{ errorMessage }}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>

  <!-- Social Media Section -->
  <div class="py-20">
    <div class="container mx-auto px-4 text-center">
      <div data-sal="slide-up" data-sal-delay="200">
        <h6 class="text-sm font-medium uppercase tracking-wider text-secondary mb-4">FOLLOW US</h6>
        <h2 class="text-3xl md:text-4xl font-bold font-heading text-gray-900 mb-8">
          Connect With Us On Social Media
        </h2>
        <div class="flex justify-center space-x-6">
          <a 
            v-for="social in socialLinks" 
            :key="social.name"
            :href="social.url" 
            target="_blank"
            class="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center text-white hover:shadow-lg transform hover:scale-110 transition-all duration-300"
          >
            <i :class="`${social.icon} text-xl`"></i>
          </a>
        </div>
      </div>
    </div>
  </div>

  <FooterComponent />
</template>

<script>
  import emailjs from "emailjs-com"
  import HeaderComponent from '@/components/Header.vue'
  import FooterComponent from '@/components/Footer.vue'
  import HeroComponent from '@/components/Hero.vue'

export default {
  name: 'ContactView',
  components: {
    HeaderComponent,
    FooterComponent,
    HeroComponent 
  },
  data() {
    return {
      form: {
        name: "",
        email: "",
        company: "",
        services: [],
        message: "",
      },
      loading: false,
      successMessage: "",
      errorMessage: "",
      contactInfo: [
        {
          title: "Email Us",
          description: "Send us an email and we'll respond within 24 hours",
          icon: "fas fa-envelope",
          value: "contact@diginova.ch",
          link: "mailto:contact@diginova.ch",
          external: false
        },
        {
          title: "Book a free meeting",
          description: "Schedule a consultation to discuss your project",
          icon: "fas fa-calendar-alt",
          value: "Schedule Now",
          link: "https://calendly.com/diginova",
          external: true
        }
      ],
      services: [
        { value: "branding", label: "Branding" },
        { value: "design", label: "Design" },
        { value: "website", label: "Website" }
      ],
      socialLinks: [
        {
          name: "Instagram",
          icon: "fab fa-instagram",
          url: "https://www.instagram.com/diginova_ch"
        },
        {
          name: "Facebook",
          icon: "fab fa-facebook-f",
          url: "https://www.facebook.com/diginova.ch"
        },
        {
          name: "LinkedIn",
          icon: "fab fa-linkedin-in",
          url: "https://www.linkedin.com/company/diginova"
        }
      ]
    };
  },
  methods: {
    async sendEmail() {
      this.loading = true;
      this.successMessage = "";
      this.errorMessage = "";

      try {
        const response = await emailjs.send(
          "service_mvh7q2i",  // Replace with your EmailJS service ID
          "template_zpg6atb", // Replace with your EmailJS template ID
          {
            from_name: this.form.name,
            from_email: this.form.email,
            company: this.form.company,
            services: this.form.services.join(", "),
            message: this.form.message,
          },
          "ZuUzLFOZMLHmoaIUv" // Replace with your EmailJS public key
        );

        if (response.status === 200) {
          this.successMessage = "Thank you! Your message has been sent successfully. We'll get back to you within 24 hours.";
          this.form = { name: "", email: "", company: "", services: [], message: "" }; // Reset form
        } else {
          throw new Error("Failed to send email");
        }
      } catch (error) {
        this.errorMessage = "Sorry, there was an error sending your message. Please try again or contact us directly at contact@diginova.ch";
      } finally {
        this.loading = false;
      }
    },
    addContactStructuredData() {
      const contactData = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Diginova",
        "url": "https://diginova.ch",
        "contactPoint": {
          "@type": "ContactPoint",
          "email": "contact@diginova.ch",
          "contactType": "customer service",
          "availableLanguage": ["English", "German", "French"]
        },
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "CH"
        }
      };
      
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(contactData);
      document.head.appendChild(script);
    }
  },
  mounted() {
    // Add structured data for contact page
    this.addContactStructuredData();
    
    // Initialize scroll animations
    if (typeof sal !== 'undefined') {
      sal({
        duration: 500,
        threshold: 0.1,
        once: true
      });
    }
  }
};
</script>

<style scoped>
</style>